import styles from './askAnything.module.css';

import { useEffect, useMemo, useState } from 'react';

import AskPanel, { Chip } from './AskPanel';
import { ASK_SCENARIOS, type AskReceipt, splitCitations } from './askScenarios';
import { tokenizeAnswer, useAskDemo } from './useAskDemo';

const cx = (...names: (string | false | undefined)[]) => names.filter(Boolean).join(' ');
const delay = (s: number) => ({ '--delay': `${s}s` }) as React.CSSProperties;

interface HeroAskAnythingProps {
  onWatchFilm?: () => void;
}

/**
 * Homepage hero: "Ask your business anything." The copy is static; the demo below it plays one complete
 * agent job per scenario (see askScenarios.ts for the content, useAskDemo.ts for the timing).
 */
const HeroAskAnything: React.FC<HeroAskAnythingProps> = ({ onWatchFilm }) => {
  const [motion, setMotion] = useState<DemoMotion>({ thinking: false, paused: false });

  return (
    <section
      className={cx(
        styles.hero,
        motion.thinking && styles.thinking,
        motion.paused && styles.paused
      )}
      data-paused={motion.paused ? 'true' : undefined}
      aria-labelledby="hero-heading">
      <div className={styles.deep} aria-hidden="true" />
      <div className={styles.glow} aria-hidden="true" />
      <div className={styles.grid} aria-hidden="true" />

      <div className={styles.center}>
        <span className={cx(styles.kicker, styles.enter)} style={delay(0.05)}>
          <b aria-hidden="true">✦</b> An AI agent trained on your own documents
        </span>
        <h1 id="hero-heading" className={styles.headline}>
          {['Ask', 'your', 'business'].map((word, i) => (
            <span key={word}>
              <span className={cx(styles.headWord, styles.enter)} style={delay(0.15 + i * 0.08)}>
                {word}
              </span>{' '}
            </span>
          ))}
          <em className={cx(styles.headWord, styles.enter)} style={delay(0.45)}>
            anything.
          </em>
        </h1>
        <p className={cx(styles.lede, styles.enter)} style={delay(0.6)}>
          It knows your files, answers your customers and your team, and shows its sources.
        </p>
        <p className={styles.srOnly}>
          Demo: a Chat.co agent answers questions on a website, in Slack, on WhatsApp in Spanish and
          in a research workspace, and cites the source documents for each answer.
        </p>

        <AskDemo onMotionChange={setMotion} onWatchFilm={onWatchFilm} />
      </div>
    </section>
  );
};

/** Hero-wide visual state: the reading glow/ring, and the pause that freezes every CSS animation. */
interface DemoMotion {
  thinking: boolean;
  paused: boolean;
}

const AskDemo: React.FC<{
  onMotionChange: (motion: DemoMotion) => void;
  onWatchFilm?: () => void;
}> = ({ onMotionChange, onWatchFilm }) => {
  const { state, reducedMotion, select, userPaused, togglePause, panelRef, panelHandlers } =
    useAskDemo(ASK_SCENARIOS);
  const scenario = ASK_SCENARIOS[state.index];
  const tokens = useMemo(() => tokenizeAnswer(scenario.answer), [scenario]);
  const reading = state.phase === 'reading';

  useEffect(() => {
    onMotionChange({ thinking: reading, paused: userPaused });
  }, [reading, userPaused, onMotionChange]);

  return (
    <>
      <AskPanel
        scenario={scenario}
        tokens={tokens}
        state={state}
        animate={!reducedMotion}
        userPaused={userPaused}
        onTogglePause={togglePause}
        panelRef={panelRef}
        panelHandlers={panelHandlers}
      />

      <div
        className={cx(styles.tabs, styles.enter)}
        style={delay(0.9)}
        role="group"
        aria-label="Demo questions">
        {ASK_SCENARIOS.map((s, i) => (
          <button
            key={s.id}
            type="button"
            className={cx(styles.tab, i === state.index && styles.tabOn)}
            aria-pressed={i === state.index}
            style={{ '--progress': i === state.index ? state.progress : 0 } as React.CSSProperties}
            onClick={() => select(i)}>
            <b>{s.tag}</b>
            {s.tabLabel}
          </button>
        ))}
      </div>

      <p className={cx(styles.sub, styles.enter)} style={delay(1)}>
        14-day trial, no credit card
        {onWatchFilm && (
          <>
            {' · '}
            <button type="button" className={styles.subLink} onClick={onWatchFilm}>
              Watch the 37-second story
            </button>
          </>
        )}
      </p>

      <ReceiptsRail receipts={state.receipts} />
    </>
  );
};

const ReceiptsRail: React.FC<{ receipts: AskReceipt[] }> = ({ receipts }) => (
  <div className={cx(styles.rail, styles.enter)} style={delay(1.2)} aria-hidden="true">
    <div className={styles.railTrack}>
      {receipts.map((r) => (
        <div
          key={r.id}
          className={cx(styles.receipt, !r.id.startsWith('seed-') && styles.receiptNew)}>
          <div className={styles.receiptChannel}>
            {r.channel}
            <span>{r.meta}</span>
          </div>
          <div className={styles.receiptQ}>{r.question}</div>
          <div className={styles.receiptA}>
            {splitCitations(r.answer).map((part, i) =>
              // eslint-disable-next-line react/no-array-index-key -- positional text runs
              typeof part === 'number' ? <Chip key={i} n={part} /> : <span key={i}>{part}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default HeroAskAnything;
